import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Music, Calendar, MapPin, Mail, Copy, Check, X, RefreshCw,
  ExternalLink, Filter, CheckCircle, Clock,
  Building2, Info, AlertTriangle,
} from 'lucide-react';
import { db, functions } from '../firebase';
import {
  collection, doc, getDocs, updateDoc, deleteDoc,
  serverTimestamp, query, orderBy,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { LABELS, OUTREACH_STATUS, generatePressEmail } from '../concertData';

// ─── Firebase callable ────────────────────────────────────────────────────────
const fetchConcertsFn = httpsCallable(functions, 'fetchConcerts', { timeout: 120000 });

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatDate(dateStr) {
  if (!dateStr) return 'Date TBA';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

// ─── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const cfg = OUTREACH_STATUS[status] || OUTREACH_STATUS.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold tracking-widest uppercase border ${cfg.color} ${cfg.bg} ${cfg.border}`}>
      {cfg.label}
    </span>
  );
}

// ─── Label Badge ──────────────────────────────────────────────────────────────
function LabelBadge({ labelKey }) {
  const label = LABELS[labelKey];
  if (!label) return null;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold border ${label.color} ${label.bg} ${label.border}`}>
      {label.name}
    </span>
  );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-5 flex items-center gap-4">
      <div className={`p-3 rounded-xl ${color} bg-opacity-10`}>
        <Icon size={20} className={color.replace('bg-', 'text-')} />
      </div>
      <div>
        <p className="text-2xl font-black text-white">{value}</p>
        <p className="text-xs tracking-widest uppercase text-neutral-500">{label}</p>
      </div>
    </div>
  );
}

// ─── Concert Card ─────────────────────────────────────────────────────────────
function ConcertCard({ concert, onOpen }) {
  const labelInfo = LABELS[concert.label]; // eslint-disable-line no-unused-vars
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="bg-neutral-900 border border-neutral-800 rounded-2xl p-5 hover:border-neutral-600 transition-colors cursor-pointer group"
      onClick={() => onOpen(concert)}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-black text-white text-lg leading-tight truncate group-hover:text-amber-400 transition-colors mb-1">
            {concert.artist}
          </h3>
          <LabelBadge labelKey={concert.label} />
        </div>
        <StatusBadge status={concert.status} />
      </div>

      <div className="space-y-1.5 mb-4">
        <div className="flex items-center gap-1.5 text-sm text-neutral-300">
          <Building2 size={13} className="text-neutral-500 shrink-0" />
          <span className="truncate">{concert.venue}</span>
        </div>
        <div className="flex items-center gap-1.5 text-sm text-neutral-400">
          <MapPin size={13} className="text-neutral-500 shrink-0" />
          <span>{concert.city}, {concert.state}</span>
        </div>
        <div className="flex items-center gap-1.5 text-sm text-neutral-400">
          <Calendar size={13} className="text-neutral-500 shrink-0" />
          <span>{formatDate(concert.date)}</span>
        </div>
      </div>

      <div className="flex items-center justify-between">
        {concert.ticketUrl ? (
          <a
            href={concert.ticketUrl}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1 text-xs text-neutral-600 hover:text-amber-400 transition-colors"
          >
            <ExternalLink size={11} /> Tickets
          </a>
        ) : (
          <span />
        )}
        <span className="text-xs text-neutral-600 group-hover:text-neutral-400 transition-colors">
          View & Pitch →
        </span>
      </div>
    </motion.div>
  );
}

// ─── Email Chip ───────────────────────────────────────────────────────────────
function EmailChip({ email, onCopy, copied }) {
  return (
    <button
      onClick={() => onCopy(email, 'pressEmail')}
      className="flex items-center gap-1.5 px-3 py-1.5 bg-neutral-800 hover:bg-neutral-700 border border-neutral-700 hover:border-amber-400/40 rounded-lg text-xs font-medium transition-colors"
    >
      {copied === 'pressEmail' ? (
        <Check size={12} className="text-emerald-400" />
      ) : (
        <Copy size={12} className="text-neutral-400" />
      )}
      <span className="text-neutral-300">{email}</span>
    </button>
  );
}

// ─── Concert Detail Modal ─────────────────────────────────────────────────────
function ConcertModal({ concert, onClose, onStatusChange, onDelete }) {
  const labelInfo = LABELS[concert.label];
  const [contactName, setContactName] = useState(concert.contactName || '');
  const [contactEmail, setContactEmail] = useState(concert.contactEmail || '');
  const [notes, setNotes] = useState(concert.notes || '');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [copied, setCopied] = useState(null);

  const generated = generatePressEmail({ ...concert, contactName, contactEmail });
  const [editableSubject, setEditableSubject] = useState(generated.subject);
  const [editableBody, setEditableBody] = useState(generated.body);

  async function copyText(text, key) {
    await navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  }

  async function handleStatusChange(newStatus) {
    await onStatusChange(concert.id, newStatus, { contactName, contactEmail, notes });
  }

  async function saveContact() {
    setSaving(true);
    await onStatusChange(concert.id, concert.status, { contactName, contactEmail, notes });
    setSaving(false);
  }

  const mailtoLink = contactEmail
    ? `mailto:${contactEmail}?subject=${encodeURIComponent(editableSubject)}&body=${encodeURIComponent(editableBody)}`
    : null;

  async function handleOpenInMail() {
    if (concert.status === 'pending') {
      await handleStatusChange('contacted');
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div className="min-h-full max-w-3xl mx-auto px-4 sm:px-6 py-8" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <span className="text-xs tracking-[0.3em] uppercase text-amber-400 font-medium">Concert Outreach</span>
            <h2 className="text-3xl font-black mt-1 text-white">{concert.artist}</h2>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <LabelBadge labelKey={concert.label} />
              <StatusBadge status={concert.status} />
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
              <span className="flex items-center gap-1.5 text-sm text-neutral-400">
                <Building2 size={13} className="text-neutral-500" /> {concert.venue}
              </span>
              <span className="flex items-center gap-1.5 text-sm text-neutral-400">
                <MapPin size={13} className="text-neutral-500" /> {concert.city}, {concert.state}
              </span>
              <span className="flex items-center gap-1.5 text-sm text-neutral-400">
                <Calendar size={13} className="text-neutral-500" /> {formatDate(concert.date)}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="shrink-0 p-2 rounded-full bg-neutral-800 hover:bg-neutral-700 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Ticket link */}
        {concert.ticketUrl && (
          <a
            href={concert.ticketUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 mb-6 text-xs text-neutral-500 hover:text-amber-400 transition-colors"
          >
            <ExternalLink size={12} /> View on Ticketmaster
          </a>
        )}

        {/* Status actions */}
        <div className="flex flex-wrap gap-2 mb-6">
          {Object.entries(OUTREACH_STATUS).map(([s, cfg]) => {
            const isActive = concert.status === s;
            return (
              <button
                key={s}
                onClick={() => handleStatusChange(s)}
                className={`px-4 py-2 rounded-full text-xs font-bold tracking-widest uppercase border transition-all ${
                  isActive
                    ? `${cfg.color} ${cfg.bg} ${cfg.border}`
                    : 'border-neutral-700 text-neutral-500 hover:border-neutral-500 hover:text-neutral-300'
                }`}
              >
                {isActive && <span className="mr-1">✓</span>}{cfg.label}
              </button>
            );
          })}
        </div>

        {/* Label info box */}
        {labelInfo && (
          <div className={`mb-6 p-4 rounded-2xl border ${labelInfo.bg} ${labelInfo.border}`}>
            <div className="flex items-center gap-2 mb-3">
              <Music size={14} className={labelInfo.color} />
              <span className={`text-sm font-black ${labelInfo.color}`}>{labelInfo.name}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-neutral-500 uppercase tracking-widest mb-1">Press Contact</p>
                <p className="text-sm text-neutral-200 font-medium">{labelInfo.pressContact}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-500 uppercase tracking-widest mb-1">Press Email</p>
                <EmailChip email={labelInfo.pressEmail} onCopy={copyText} copied={copied} />
              </div>
              <div>
                <p className="text-xs text-neutral-500 uppercase tracking-widest mb-1">Website</p>
                <a
                  href={labelInfo.website}
                  target="_blank"
                  rel="noreferrer"
                  className={`flex items-center gap-1 text-sm font-medium ${labelInfo.color} hover:underline`}
                >
                  {labelInfo.website.replace('https://', '')} <ExternalLink size={11} />
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Contact + Notes */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
          <div>
            <label className="block text-xs text-neutral-500 uppercase tracking-widest mb-1.5">Contact Name</label>
            <input
              type="text"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              placeholder="Press contact / PR name"
              className="w-full bg-neutral-900 border border-neutral-700 rounded-xl px-3 py-2.5 text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-amber-400 transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs text-neutral-500 uppercase tracking-widest mb-1.5">Contact Email</label>
            <input
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder={labelInfo?.pressEmail || 'press@label.com'}
              className="w-full bg-neutral-900 border border-neutral-700 rounded-xl px-3 py-2.5 text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-amber-400 transition-colors"
            />
          </div>
        </div>
        <div className="mb-3">
          <label className="block text-xs text-neutral-500 uppercase tracking-widest mb-1.5">Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Any follow-up notes, response details, etc."
            className="w-full bg-neutral-900 border border-neutral-700 rounded-xl px-3 py-2.5 text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-amber-400 transition-colors resize-none"
          />
        </div>
        <button
          onClick={saveContact}
          disabled={saving}
          className="mb-6 text-xs text-neutral-500 hover:text-amber-400 transition-colors disabled:opacity-40"
        >
          {saving ? 'Saving…' : '↑ Save contact & notes'}
        </button>

        {/* Pre-fill tip */}
        {!contactEmail && labelInfo && (
          <div className="mb-4 p-3 bg-amber-400/5 border border-amber-400/20 rounded-xl flex items-start gap-2">
            <Info size={13} className="text-amber-400 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-400/80">
              No contact email set. Copy the label's press email above or enter a custom one to enable "Open in Mail".
            </p>
          </div>
        )}

        {/* Generated Pitch Email */}
        <div className="border border-neutral-800 rounded-2xl overflow-hidden">
          <div className="bg-neutral-900 px-5 py-3 flex items-center justify-between border-b border-neutral-800 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Mail size={15} className="text-amber-400" />
              <span className="text-sm font-bold">Generated Pitch Email</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => copyText(`Subject: ${editableSubject}\n\n${editableBody}`, 'all')}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-neutral-800 hover:bg-neutral-700 rounded-lg text-xs font-medium transition-colors"
              >
                {copied === 'all' ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
                {copied === 'all' ? 'Copied!' : 'Copy All'}
              </button>
              {mailtoLink ? (
                <a
                  href={mailtoLink}
                  onClick={handleOpenInMail}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-amber-400 hover:bg-amber-300 text-black rounded-lg text-xs font-bold transition-colors"
                >
                  <ExternalLink size={13} /> Open in Mail
                </a>
              ) : (
                <span className="text-xs text-neutral-600 italic">Add contact email above to send</span>
              )}
            </div>
          </div>

          {/* Subject */}
          <div className="px-5 py-3 bg-neutral-950 border-b border-neutral-800">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <span className="text-xs text-neutral-500 uppercase tracking-widest">Subject</span>
                <input
                  type="text"
                  value={editableSubject}
                  onChange={(e) => setEditableSubject(e.target.value)}
                  className="w-full bg-transparent text-sm text-white mt-0.5 focus:outline-none focus:text-amber-100"
                />
              </div>
              <button
                onClick={() => copyText(editableSubject, 'subject')}
                className="shrink-0 p-1.5 text-neutral-500 hover:text-white transition-colors"
              >
                {copied === 'subject' ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="relative">
            <textarea
              value={editableBody}
              onChange={(e) => setEditableBody(e.target.value)}
              className="w-full px-5 py-5 text-sm text-neutral-300 leading-relaxed bg-transparent focus:outline-none resize-none max-h-[500px] overflow-y-auto font-sans"
              rows={editableBody.split('\n').length + 1}
            />
            <button
              onClick={() => copyText(editableBody, 'body')}
              className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 bg-neutral-800/90 hover:bg-neutral-700 rounded-lg text-xs font-medium transition-colors backdrop-blur-sm"
            >
              {copied === 'body' ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
              {copied === 'body' ? 'Copied!' : 'Copy Body'}
            </button>
          </div>
        </div>

        {/* Delete */}
        <div className="mt-6 pt-6 border-t border-neutral-800">
          {!confirmDelete ? (
            <button
              onClick={() => setConfirmDelete(true)}
              className="text-xs text-neutral-700 hover:text-red-400 transition-colors"
            >
              Remove this show
            </button>
          ) : (
            <div className="flex items-center gap-3">
              <span className="text-xs text-neutral-400">Are you sure?</span>
              <button onClick={() => onDelete(concert.id)} className="text-xs text-red-400 font-bold hover:text-red-300 transition-colors">Yes, delete</button>
              <button onClick={() => setConfirmDelete(false)} className="text-xs text-neutral-500 hover:text-white transition-colors">Cancel</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────

export default function ConcertOutreach() {
  const [concerts, setConcerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeConcert, setActiveConcert] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null);

  async function loadConcerts() {
    try {
      const q = query(collection(db, 'concertOutreach'), orderBy('date', 'asc'));
      const snap = await getDocs(q);
      setConcerts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Firestore load error:', err);
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadConcerts(); }, []);

  const handleStatusChange = useCallback(async (concertId, newStatus, extra = {}) => {
    const ref = doc(db, 'concertOutreach', concertId);
    await updateDoc(ref, { status: newStatus, updatedAt: serverTimestamp(), ...extra });
    setConcerts((prev) =>
      prev.map((c) => (c.id === concertId ? { ...c, status: newStatus, ...extra } : c))
    );
    if (activeConcert?.id === concertId) {
      setActiveConcert((prev) => ({ ...prev, status: newStatus, ...extra }));
    }
  }, [activeConcert]);

  const handleDelete = useCallback(async (concertId) => {
    await deleteDoc(doc(db, 'concertOutreach', concertId));
    setConcerts((prev) => prev.filter((c) => c.id !== concertId));
    setActiveConcert(null);
  }, []);

  async function handleRefresh() {
    setRefreshing(true);
    setRefreshResult(null);
    try {
      const result = await fetchConcertsFn({});
      setRefreshResult(result.data);
      // Reload from Firestore to pick up newly added shows
      setLoading(true);
      await loadConcerts();
    } catch (err) {
      console.error('fetchConcerts error:', err);
      setRefreshResult({ error: err.message || 'Failed to fetch concerts. Make sure TICKETMASTER_KEY secret is set.' });
    }
    setRefreshing(false);
  }

  // ── Filtered list ──────────────────────────────────────────────────────────
  const filtered = concerts.filter((c) => {
    const matchLabel = filter === 'all' || c.label === filter;
    const matchStatus = statusFilter === 'all' || c.status === statusFilter;
    return matchLabel && matchStatus;
  });

  // ── Stats ──────────────────────────────────────────────────────────────────
  const counts = concerts.reduce((acc, c) => {
    acc.total = (acc.total || 0) + 1;
    acc[c.status] = (acc[c.status] || 0) + 1;
    acc[`label_${c.label}`] = (acc[`label_${c.label}`] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-black text-white pt-16 sm:pt-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">

        {/* Header */}
        <div className="mb-10 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <span className="text-xs tracking-[0.3em] uppercase text-amber-400 font-medium">Black Magic Films</span>
            <h1 className="text-4xl sm:text-5xl font-black mt-2 mb-2">Concert Outreach</h1>
            <p className="text-neutral-400 max-w-xl">
              Upcoming metal, rock, and punk shows in NJ &amp; Philadelphia. Request media passes from label PR contacts.
            </p>
          </div>
          <div className="flex flex-col items-end gap-3 shrink-0">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-5 py-3 bg-amber-400 hover:bg-amber-300 disabled:opacity-50 disabled:cursor-not-allowed text-black font-black rounded-xl transition-colors text-sm"
            >
              <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? 'Fetching Shows…' : 'Refresh Shows'}
            </button>
            {refreshResult && !refreshResult.error && (
              <p className="text-xs text-emerald-400 font-medium">
                {refreshResult.added === 0
                  ? 'No new shows found.'
                  : `${refreshResult.added} new show${refreshResult.added !== 1 ? 's' : ''} added!`}
                {refreshResult.skipped > 0 && <span className="text-neutral-500 ml-1">({refreshResult.skipped} already saved)</span>}
              </p>
            )}
            {refreshResult?.error && (
              <p className="text-xs text-red-400 max-w-xs text-right">{refreshResult.error}</p>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <StatCard label="Total Shows"   value={counts.total || 0}           color="bg-neutral-400"  icon={Music}       />
          <StatCard label="Contacted"     value={counts.contacted || 0}       color="bg-blue-400"     icon={Mail}        />
          <StatCard label="Approved"      value={counts.approved || 0}        color="bg-emerald-400"  icon={CheckCircle} />
          <StatCard label="No Response"   value={counts.noResponse || 0}      color="bg-amber-400"    icon={Clock}       />
        </div>

        {/* Per-label counts */}
        <div className="flex flex-wrap gap-3 mb-10">
          {Object.entries(LABELS).map(([key, label]) => {
            const n = counts[`label_${key}`] || 0;
            return (
              <div key={key} className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-bold ${label.bg} ${label.border}`}>
                <span className={label.color}>{label.name}</span>
                <span className="text-neutral-400">{n} show{n !== 1 ? 's' : ''}</span>
              </div>
            );
          })}
        </div>

        {/* Label filter tabs */}
        <div className="mb-4">
          <p className="text-xs text-neutral-500 uppercase tracking-widest mb-2 flex items-center gap-1.5">
            <Filter size={11} /> Label
          </p>
          <div className="flex flex-wrap gap-2">
            {[['all', 'All Labels', null], ...Object.entries(LABELS).map(([k, v]) => [k, v.name, v])].map(([key, name, labelData]) => {
              const isActive = filter === key;
              return (
                <button
                  key={key}
                  onClick={() => setFilter(key)}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase border transition-all ${
                    isActive
                      ? labelData
                        ? `${labelData.color} ${labelData.bg} ${labelData.border}`
                        : 'bg-white text-black border-white'
                      : 'border-neutral-700 text-neutral-500 hover:border-neutral-500 hover:text-white'
                  }`}
                >
                  {name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Status filter tabs */}
        <div className="mb-8">
          <p className="text-xs text-neutral-500 uppercase tracking-widest mb-2 flex items-center gap-1.5">
            <Filter size={11} /> Status
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase border transition-all ${
                statusFilter === 'all'
                  ? 'bg-white text-black border-white'
                  : 'border-neutral-700 text-neutral-500 hover:border-neutral-500 hover:text-white'
              }`}
            >
              All ({concerts.length})
            </button>
            {Object.entries(OUTREACH_STATUS).map(([s, cfg]) => {
              const n = counts[s] || 0;
              const isActive = statusFilter === s;
              return (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase border transition-all ${
                    isActive
                      ? `${cfg.color} ${cfg.bg} ${cfg.border}`
                      : 'border-neutral-700 text-neutral-500 hover:border-neutral-500 hover:text-white'
                  }`}
                >
                  {cfg.label} ({n})
                </button>
              );
            })}
          </div>
        </div>

        {/* Concert grid */}
        {loading ? (
          <div className="text-center py-20 text-neutral-600">Loading shows…</div>
        ) : loadError ? (
          <div className="text-center py-20">
            <AlertTriangle size={40} className="text-red-400 mx-auto mb-4" />
            <p className="text-red-400 font-bold mb-2">Failed to load concerts</p>
            <p className="text-neutral-500 text-sm font-mono">{loadError}</p>
            <p className="text-neutral-600 text-xs mt-4">Check Firestore rules: <code>firebase deploy --only firestore</code></p>
          </div>
        ) : concerts.length === 0 ? (
          <div className="text-center py-20">
            <Music size={48} className="text-neutral-800 mx-auto mb-4" />
            <p className="text-neutral-400 font-bold text-lg mb-2">No shows found yet</p>
            <p className="text-neutral-600 text-sm mb-6">
              Hit "Refresh Shows" to pull upcoming dates from Ticketmaster
            </p>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="px-6 py-3 bg-amber-400 hover:bg-amber-300 disabled:opacity-50 text-black font-black rounded-xl transition-colors flex items-center gap-2 mx-auto"
            >
              <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? 'Fetching…' : 'Refresh Shows'}
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Music size={40} className="text-neutral-800 mx-auto mb-4" />
            <p className="text-neutral-500 font-bold mb-2">No shows match the current filters</p>
            <button
              onClick={() => { setFilter('all'); setStatusFilter('all'); }}
              className="mt-3 text-xs text-amber-400 hover:text-amber-300 transition-colors"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <AnimatePresence>
              {filtered.map((concert) => (
                <ConcertCard key={concert.id} concert={concert} onOpen={setActiveConcert} />
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </div>

      <AnimatePresence>
        {activeConcert && (
          <ConcertModal
            concert={activeConcert}
            onClose={() => setActiveConcert(null)}
            onStatusChange={handleStatusChange}
            onDelete={handleDelete}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
